import React, { type ReactNode } from 'react';
import {
  type DrawerMenuItem,
  type NavItemNoIcon,
  navItemType,
  type OddsFormat,
  type SiteConfig,
  variant,
} from '@cx-web/domain-types';
import classnames from 'classnames';

import { type Repub } from '@cx-modules/constants';

import { DesktopConfigItems } from '../DesktopConfigItems/index';
import { HeaderNavItem } from '../HeaderNavItem/index';
import { PrimaryHeaderLeftMenuItems } from '../PrimaryHeaderLeftMenuItems/index';
import { ScrollableNav } from '../ScrollableNav/index';

import styles from './PrimaryHeaderNavGroup.module.css';

type PrimaryHeaderNavGroupProps = {
  betslipSlot?: ReactNode;
  firstLevelNav: NavItemNoIcon[];
  isLoggedIn: boolean;
  isUkStyle?: boolean;
  logoutLabel: string;
  mobileHamburgerNav: DrawerMenuItem[];
  navigationKey?: string;
  oddsFormat: OddsFormat;
  onLogout: () => void;
  onOddsFormatChange: (format: OddsFormat) => void;
  onStateChange?: (state: string) => void;
  renderAccountMenu?: (variant: 'desktop' | 'mobile') => ReactNode;
  repub?: Repub;
  selectedState?: string;
  serverUrl: string;
  siteConfig: SiteConfig;
  stateOptions?: Array<{ code: string; label: string }>;
};

const PrimaryHeaderNavGroup = ({
  betslipSlot,
  firstLevelNav,
  isLoggedIn,
  isUkStyle,
  logoutLabel,
  mobileHamburgerNav,
  navigationKey,
  oddsFormat,
  onLogout,
  onOddsFormatChange,
  onStateChange,
  renderAccountMenu,
  repub,
  selectedState,
  serverUrl,
  siteConfig,
  stateOptions,
}: PrimaryHeaderNavGroupProps) => {
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const links = firstLevelNav;
  const filteredLinks = React.useMemo(
    () =>
      links.filter(
        link =>
          link.type !== navItemType.STATE_SELECTOR &&
          link.type !== navItemType.ODDS_FORMAT &&
          link.type !== navItemType.MY_ACCOUNT &&
          link.type !== navItemType.BETSLIP &&
          link.type !== navItemType.TOOLS,
      ),
    [links],
  );

  const shouldRenderOddsFormatSelector = links.some(link => link.type === navItemType.ODDS_FORMAT);
  const shouldRenderAccountMenu = links.some(link => link.type === navItemType.MY_ACCOUNT);
  const shouldRenderBetslip = links.some(link => link.type === navItemType.BETSLIP);

  return (
    <section className={classnames(styles.container, isUkStyle && styles.uk)} data-testid="primary-header-nav-section">
      <nav className={styles.content}>
        {isUkStyle ? (
          <PrimaryHeaderLeftMenuItems
            dropdownRef={dropdownRef}
            links={filteredLinks}
            serverUrl={serverUrl}
            siteConfig={siteConfig}
          />
        ) : (
          <ScrollableNav
            autoScrollToSelected
            navigationKey={navigationKey}
            style={{ '--fade-bg-color': 'var(--cxy-color-midnight-blue-400)' } as React.CSSProperties}
            testId="primary-header-nav"
          >
            {filteredLinks.map(link => (
              <React.Fragment key={link.link}>
                <HeaderNavItem
                  {...link}
                  bookmakers={siteConfig.bookmakers}
                  dropdownRef={dropdownRef}
                  isUkStyle={isUkStyle}
                  serverUrl={serverUrl}
                  variant={variant.primary}
                />
              </React.Fragment>
            ))}
          </ScrollableNav>
        )}
        <div>
          <DesktopConfigItems
            betslipSlot={shouldRenderBetslip ? betslipSlot : null}
            isLoggedIn={isLoggedIn}
            isUkStyle={isUkStyle}
            links={links}
            logoutLabel={logoutLabel}
            mobileHamburgerNav={mobileHamburgerNav}
            oddsFormat={oddsFormat}
            oddsFormatOptions={siteConfig.oddsFormat}
            onLogout={onLogout}
            onOddsFormatChange={onOddsFormatChange}
            onStateChange={onStateChange}
            renderAccountMenu={shouldRenderAccountMenu ? renderAccountMenu : null}
            repub={repub}
            selectedState={selectedState}
            shouldRenderOddsFormatSelector={shouldRenderOddsFormatSelector}
            stateOptions={stateOptions}
          />
        </div>
        <div className={styles.dropdownWrapper} ref={dropdownRef} />
      </nav>
    </section>
  );
};

export { PrimaryHeaderNavGroup };
